import { MAX_CAPITAL_RAISED_FALLBACK } from "constants/benchmarking";
import { BENCHMARK_DATASET } from "constants/enums";

import { SavedBenchmarkJob } from "./savedJobs";

/**
 * Normalizes a value for comparison - handles null/undefined/empty strings
 */
function normalizeValue(val: any): string {
  if (val === null || val === undefined || val === "") return "";
  return String(val).toLowerCase().trim();
}

/**
 * Checks if a saved benchmark job matches the current search parameters
 * This is the single source of truth for duplicate detection
 */
export function isSavedJobDuplicate(
  savedJob: SavedBenchmarkJob | any,
  dataset: string,
  jobTitle: string,
  location?: string,
  searchParams?: Record<string, any>,
): boolean {
  // Must match dataset
  if (savedJob.dataset !== dataset) return false;

  const savedParams = (savedJob.searchParams || {}) as Record<string, any>;
  const currentParams = searchParams || {};

  // For Comptryx/Mercer, check key parameters including all filters
  if (dataset === BENCHMARK_DATASET.comptryx || dataset === BENCHMARK_DATASET.mercer) {
    return (
      normalizeValue(savedParams.family) === normalizeValue(currentParams.family) &&
      normalizeValue(savedParams.subFamily) === normalizeValue(currentParams.subFamily) &&
      normalizeValue(savedParams.level) === normalizeValue(currentParams.level) &&
      normalizeValue(savedParams.specialization) === normalizeValue(currentParams.specialization) &&
      normalizeValue(savedParams.tier) === normalizeValue(currentParams.tier) &&
      normalizeValue(savedParams.sector) === normalizeValue(currentParams.sector) &&
      normalizeValue(savedParams.supersector) === normalizeValue(currentParams.supersector) &&
      normalizeValue(savedParams.revenue) === normalizeValue(currentParams.revenue) &&
      normalizeValue(savedParams.employeeCount) === normalizeValue(currentParams.employeeCount) &&
      normalizeValue(savedParams.companySize) === normalizeValue(currentParams.companySize) &&
      normalizeValue(savedParams.city) === normalizeValue(currentParams.city) &&
      normalizeValue(savedParams.state) === normalizeValue(currentParams.state) &&
      normalizeValue(savedParams.geo) === normalizeValue(currentParams.geo)
    );
  }

  // For Comprehensive, check relevant filters
  if (dataset === BENCHMARK_DATASET.comprehensive) {
    return (
      normalizeValue(savedParams.jobFamily) === normalizeValue(currentParams.jobFamily) &&
      normalizeValue(savedParams.level) === normalizeValue(currentParams.level) &&
      normalizeValue(savedParams.geo) === normalizeValue(currentParams.geo) &&
      normalizeValue(savedParams.stage) === normalizeValue(currentParams.stage) &&
      normalizeValue(savedParams.headcount) === normalizeValue(currentParams.headcount) &&
      normalizeValue(savedParams.capitalRaised) === normalizeValue(currentParams.capitalRaised) &&
      normalizeValue(savedParams.yoe) === normalizeValue(currentParams.yoe) &&
      normalizeValue(savedParams.activeWithin) === normalizeValue(currentParams.activeWithin)
    );
  }

  // For Salary.com and others, check relevant filters
  return (
    normalizeValue(savedJob.jobTitle) === normalizeValue(jobTitle) &&
    normalizeValue(savedJob.location) === normalizeValue(location) &&
    normalizeValue(savedParams.country) === normalizeValue(currentParams.country) &&
    normalizeValue(savedParams.state) === normalizeValue(currentParams.state) &&
    normalizeValue(savedParams.city) === normalizeValue(currentParams.city) &&
    normalizeValue(savedParams.companySize) === normalizeValue(currentParams.companySize) &&
    normalizeValue(savedParams.revenue) === normalizeValue(currentParams.revenue) &&
    normalizeValue(savedParams.yearsOfExperience) ===
      normalizeValue(currentParams.yearsOfExperience) &&
    normalizeValue(savedParams.naicsCode) === normalizeValue(currentParams.naicsCode) &&
    normalizeValue(savedParams.industry) === normalizeValue(currentParams.industry)
  );
}

/**
 * Constructs a navigation URL for a saved benchmark job
 *
 * URL Structure by dataset:
 * - Salary.com: /benchmarking/salary?title=X&location=Y (query params)
 * - Comprehensive: /benchmarking/s/jobFamily=X&level=Y (path params)
 * - Mercer/Comptryx: /benchmarking/[dataset]/jobFamily=X&level=Y (path params)
 */
export function getSavedJobNavigationUrl(job: SavedBenchmarkJob): string {
  const datasetPath = job.dataset === "comprehensive" ? "s" : job.dataset || "salary";
  const baseUrl = `/benchmarking/${datasetPath}`;

  const isComptryx = job.dataset === "comptryx";
  const isMercer = job.dataset === "mercer";
  const isComprehensive = job.dataset === "comprehensive";

  // Build parameters
  const params = new URLSearchParams();

  if (job.searchParams) {
    Object.entries(job.searchParams).forEach(([key, value]) => {
      // Skip notes and originalJobTitle for all datasets
      if (key === "notes" || key === "originalJobTitle") {
        return;
      }

      // Skip requireRanges if it's false (default value)
      if (key === "requireRanges" && value === false) {
        return;
      }

      // Skip title for Comptryx and Mercer (they use family/subfamily/level)
      if ((isComptryx || isMercer) && key === "title") {
        return;
      }

      // Map "sector" to "Industry Segment" for Comptryx (URL expects this format)
      // UPDATE: We're now using "sector" instead of "Industry Segment" - pjc
      if (isComptryx && key === "sector") {
        if (value !== undefined && value !== null && value !== "") {
          params.set("sector", String(value));
        }
        return;
      }

      // Map "revenue" to "Company Size" for Comptryx
      // UPDATE: We're now using "revenue" instead of "Company Size" - pjc
      // Note: We'll fix the encoding later to use + instead of %20
      if (isComptryx && key === "revenue") {
        if (value !== undefined && value !== null && value !== "") {
          params.set("revenue", String(value));
        }
        return;
      }

      // Skip companySize for Comptryx (it's handled differently)
      if (isComptryx && key === "companySize") {
        // companySize is a separate parameter from revenue/"Company Size"
        // For now, we'll include it as-is unless it conflicts
        // TODO: Verify if companySize should be included or mapped differently
      }

      // Only add non-empty values
      if (value !== undefined && value !== null && value !== "") {
        // Special handling for capitalRaised array format [min, max] or [min, null]
        if (key === "capitalRaised" && Array.isArray(value)) {
          if (value.length === 2 && value[0] != null) {
            // Convert array to range string format
            if (value[1] != null) {
              params.set(key, `${value[0]}-${value[1]}`);
            } else {
              // If max is null, use a high default value
              // This handles the [50000000, null] case
              params.set(key, `${value[0]}-${MAX_CAPITAL_RAISED_FALLBACK}`);
            }
          }
        } else if (Array.isArray(value)) {
          // Handle other arrays (like location for Comprehensive which can have multiple values)
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== "") {
              params.append(key, String(item));
            }
          });
        } else {
          params.set(key, String(value));
        }
      }
    });
  }

  // For Salary.com and other query-parameter based datasets
  if (!isComptryx && !isMercer && !isComprehensive) {
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }

  // For path-based datasets (Mercer, Comptryx, Comprehensive)
  // The parameters go in the path segment, not as query params
  // IMPORTANT: We need the exact format that matches what the dropdown creates

  // Convert URLSearchParams to the path format
  // URLSearchParams already encodes values, but uses + for spaces
  let pathSegment = params.toString();

  if (!pathSegment) {
    return baseUrl;
  }

  // For Comprehensive, keep + for spaces but we still need to handle special chars
  // For Mercer/Comptryx, replace + with %20 before double-encoding
  if (isMercer || isComptryx) {
    // Replace + with %20 for spaces (these datasets need %20)
    pathSegment = pathSegment.replace(/\+/g, "%20");

    // For Comptryx, we need to preserve "Company+Size" with the + sign
    // After replacing all + with %20, we need to fix Company%20Size back to Company+Size
    if (isComptryx) {
      pathSegment = pathSegment.replace(/Company%20Size/g, "Company+Size");
    }
  }

  // Mercer and Comptryx need full double-encoding
  if (isMercer || isComptryx) {
    // Split by & to process each parameter
    const paramPairs = pathSegment.split("&");
    const processedPairs = paramPairs.map((pair) => {
      const equalIndex = pair.indexOf("=");
      if (equalIndex === -1) {
        return pair; // No value, just key
      }

      const key = pair.substring(0, equalIndex);
      const value = pair.substring(equalIndex + 1);

      // The value is already encoded once by URLSearchParams
      // We need to encode it again so it survives the router's decoding
      // This ensures special characters like & in values don't break the URL
      return `${key}=${encodeURIComponent(value)}`;
    });

    pathSegment = processedPairs.join("&");
  } else if (isComprehensive) {
    // Comprehensive needs to double-encode special characters
    // to survive Next.js's automatic URL decoding
    pathSegment = pathSegment
      .replace(/%26/g, "%2526") // & character
      .replace(/%2B/g, "%252B"); // + character (to preserve "5+ years" etc)
  }

  return `${baseUrl}/${pathSegment}`;
}
